import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { baseAPI } from "../config";
import { 
  KeyIcon,
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  ClockIcon,
  ClipboardIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import moment from "moment";

const ApiKeyModal = ({ apiKey, onClose }) => {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        toast.success("Clave API copiada al portapapeles", {
          position: "top-right",
          autoClose: 2000,
        });
      },
      () => {
        toast.error("Error al copiar la clave API", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
          <div className="absolute right-0 top-0 pr-4 pt-4">
            <button
              type="button"
              className="rounded-lg bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
              onClick={onClose}
            >
              <span className="sr-only">Cerrar</span>
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
              <h3 className="text-lg font-semibold leading-6 text-gray-900">
                Nueva Clave API Generada
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Esta es la única vez que podrás ver esta clave API. Por favor, guárdala en un lugar seguro.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center">
              <code className="flex-1 px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 text-sm font-mono text-gray-900 break-all">
                {apiKey}
              </code>
              <button
                type="button"
                onClick={() => copyToClipboard(apiKey)}
                className="ml-2 inline-flex items-center px-2.5 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ClipboardIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mt-6 sm:mt-8 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="inline-flex w-full justify-center rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
              onClick={onClose}
            >
              Entendido
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, keyName }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
              <h3 className="text-lg font-semibold leading-6 text-gray-900">
                Eliminar Clave API
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  ¿Estás seguro de que quieres eliminar la clave API "{keyName}"? Esta acción no se puede deshacer.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 sm:mt-8 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="inline-flex w-full justify-center rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
              onClick={onConfirm}
            >
              Eliminar
            </button>
            <button
              type="button"
              className="mt-3 inline-flex w-full justify-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              onClick={onClose}
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ApiKeysManager = ({ userId }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [newlyGeneratedKey, setNewlyGeneratedKey] = useState(null);
  const [keyToDelete, setKeyToDelete] = useState(null);

  const loadApiKeys = useCallback(async () => {
    setIsLoadingKeys(true);
    try {
      const token = localStorage.getItem("token");
      const headers = {
        Authorization: `Bearer ${token}`
      };

      const response = await axios.get(
        `${baseAPI}/api-keys/${userId}`,
        { headers }
      );

      setApiKeys(response.data);
    } catch (error) {
      console.error("Error loading API keys:", error);
      console.error("Error details:", error.response?.data);
      toast.error("Error al cargar las claves API", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsLoadingKeys(false);
    }
  }, [userId]);

  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  const generateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error("Por favor, introduce un nombre para la clave API", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsGeneratingKey(true);
    try {
      const token = localStorage.getItem("token");
      const headers = {
        Authorization: `Bearer ${token}`
      };

      const response = await axios.post(
        `${baseAPI}/api-keys`,
        { 
          name: newKeyName,
          userId
        },
        { headers }
      );
      
      if (response.data && response.data.apiKey) {
        setNewlyGeneratedKey(response.data.apiKey);
      } else {
        console.warn('API response did not contain a key:', response.data);
        toast.error("Error: No se recibió la clave API", {
          position: "top-right",
          autoClose: 3000,
        });
      }

      await loadApiKeys();
      setNewKeyName("");
      toast.success("Clave API generada correctamente", {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error generating API key:", error);
      console.error("Error details:", error.response?.data);
      toast.error("Error al generar la clave API", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsGeneratingKey(false);
    }
  };

  const handleDeleteClick = (key) => {
    setKeyToDelete(key);
  };

  const handleDeleteConfirm = async () => {
    if (!keyToDelete) return;

    try {
      const token = localStorage.getItem("token");
      const headers = {
        Authorization: `Bearer ${token}`
      };

      await axios.delete(
        `${baseAPI}/api-keys/${keyToDelete._id}`,
        { headers }
      );

      await loadApiKeys();
      toast.success("Clave API eliminada correctamente", {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error deleting API key:", error);
      console.error("Error details:", error.response?.data);
      toast.error("Error al eliminar la clave API", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setKeyToDelete(null);
    }
  };

  if (!userId) return null;

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <KeyIcon className="h-5 w-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-medium text-slate-900">Claves API</h3>
          </div>
          <a
            href="https://api.claimup.io/v1"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-150"
          >
            <DocumentTextIcon className="h-4 w-4 mr-1.5" />
            Ver Documentación
          </a>
        </div>
      </div>
      <div className="p-6">
        {/* Generate New Key Form */}
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <input
              type="text"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="Nombre de la clave API"
              className="flex-1 px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm"
            />
            <button
              type="button"
              onClick={generateApiKey}
              disabled={isGeneratingKey}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGeneratingKey ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generando...
                </>
              ) : (
                <>
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Generar Nueva Clave
                </>
              )}
            </button>
          </div>
        </div>

        {/* API Keys List */}
        <div className="space-y-4">
          {isLoadingKeys ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-sm text-slate-500 mt-2">Cargando claves API...</p>
            </div>
          ) : apiKeys.length > 0 ? (
            apiKeys.map((key) => (
              <div
                key={key._id}
                className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <KeyIcon className="h-5 w-5 text-slate-400" />
                    <div>
                      <h4 className="text-sm font-medium text-slate-900">{key.name}</h4>
                      <div className="flex items-center text-xs text-slate-500 mt-1">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        Creada el {moment(key.created_at).format("DD/MM/YYYY HH:mm")}
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteClick(key)}
                  className="ml-4 text-red-500 hover:text-red-700"
                  title="Eliminar clave"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <KeyIcon className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <h4 className="text-sm font-medium text-slate-900">No hay claves API</h4>
              <p className="text-sm text-slate-500 mt-1">
                Genera una nueva clave API para empezar a usar la API
              </p>
            </div>
          )}
        </div>
      </div>

      {/* API Key Modal */}
      {newlyGeneratedKey && (
        <ApiKeyModal
          apiKey={newlyGeneratedKey}
          onClose={() => {
            setNewlyGeneratedKey(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={!!keyToDelete}
        onClose={() => setKeyToDelete(null)}
        onConfirm={handleDeleteConfirm}
        keyName={keyToDelete?.name}
      />
    </div>
  );
};

export default ApiKeysManager; 